import { useContext, useState } from 'react';
import { Grid, Button } from "@material-ui/core";
import { FilterNone } from '@material-ui/icons';
import Link from "next/link";
import {makeStyles} from "@material-ui/styles";
import {TrackingContext} from "../providers/tracking";
import {useTranslation} from "tools/i18n";
import LazyHydrate from "tools/lazyHydrate";
import { TrackVisibility } from 'tools/TrackVisibility';
import ShoppingBagFill from "public/images/icons/shopping-bag-fill.svg";
import Envelope from "public/images/icons/meddela-mig-fill.svg";
import theme from "../theme";
import Cookies from 'js-cookie';
import ProductLabels from '../page/product/partial/labels';
import LikeButton from './wishlist/LikeButton';
import useCartStore from '@services/stores/CartStore';
import useSessionStore from '@services/stores/SessionStore';
import { elevateAddToCartNotification } from '@tools/elevateClient';
import useApplicationStore from '@services/stores/ApplicationStore';
import StockIndicator from '@components/products/partials/StockIndicator';
import ProductTooling from '@components/products/partials/ProductTooling';
import StorefrontApiService from '@services/StorefrontApiService';
import { useRouter } from 'next/router';
import PhysicalStoreStockIndicator from '@components/page/product/partial/PhysicalStoreStockIndicator';

const useStyles = makeStyles(() => ({
	item: {
		display: 'flex',
		'& > div': {
			width: '100%'
		},
		'.autoWidth &': {
			maxWidth: 'none',
			flexGrow: 1
		}
	},
	link: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		maxWidth: '100%',
		textDecoration: 'none',
		backgroundColor: theme.palette.primary.background,
		color: 'inherit',
        '& .sold-out-label': {
            display: 'none',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 90,
            background: 'rgba(0, 0, 0, 0.4)',
            alignItems: 'center',
            justifyContent: 'center',
            '& span': {
                letterSpacing: 2,
                padding: 10,
                background: '#151515',
                color: '#fff',
                width: '100%',
                fontSize: '2em',
                textTransform: 'uppercase',
                textAlign: 'center',
            },
            '&.bfSoldOut': {
                display: 'flex',
            },
            '&.bfComingSoon': {
                display: 'flex',
            },
        },
	},
	imageWrapper: {
		position: 'relative',
		padding: '0',
		overflow: 'hidden',
		'&:after': {
			content: '""',
			position: 'absolute',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			pointerEvents: 'none',
			mixBlendMode: 'multiply',
			boxShadow: 'inset 0 0 90px 8px rgb(240, 240, 240, 0.75)'
		}
	},
	variantList: {
		display: 'none',
		padding: 0,
		margin: '12px 0 0',
		fontSize: 12,
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		listStyle: 'none',
		[theme.breakpoints.down('sm')]: {
			marginTop: 6,
			fontSize: 10
		}
	},
	variant: {
		width: 'auto',
		marginRight: 4,
		padding: '2px 8px',
		backgroundColor: theme.palette.primary.background,
		border: '1px solid #ccc'
	},
	selectedVariant: {
		border: '1px solid #444'
	},
	info: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		flexGrow: 1,
		overflow: 'hidden',
		padding: '14px 10px 20px 10px'
	},
	manufacturer: {
		fontFamily: 'Chronicle, "Open Sans", sans-serif',
		textTransform: 'uppercase',
		fontSize: 11,
		fontWeight: 400,
		letterSpacing: 0,
		textAlign: 'center',
		lineHeight: 1.5,
		marginBottom: 8,
		fontSmoothing: 'auto',
		[theme.breakpoints.down('sm')]: {
			fontSize: 10
		}
	},
	moreVariantLabels: {
		display: 'flex',
		alignItems: 'center',
		position: 'absolute',
		left: 12,
		bottom: 15,
		zIndex: 10,
		padding: '4px 6px',
		color: 'rgba(0,0,0,0.65)',
		fontSize: 12,
		fontWeight: 500,
		backgroundColor: 'transparent',
		'&.light': {
			color: '#fff',
			backgroundColor: 'transparent',
			filter: 'drop-shadow(0px 0px 1px #000)',
		},
		'&.in-cart': {
			opacity: 0,
		},
		[theme.breakpoints.down('sm')]: {
			bottom: 13,
			left: 5,
			fontSize: 11
		}
	},
	name: {
		margin: '0 0 8px',
    	maxWidth: '100%',
		fontSize: 16,
		fontWeight: 400,
		color: theme.palette.primary.lowlight,
		textAlign: 'center',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		fontSmoothing: 'auto',
		[theme.breakpoints.down('sm')]: {
			fontSize: 15,
			fontWeight: 400,
			marginBottom: 6
		}
	},
	prices: {
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'center',
		alignItems: 'center'
	},
	price: {
		fontSize: 16,
		fontWeight: 500,
		lineHeight: 1.5,
		fontSmoothing: 'auto',
		color: theme.palette.primary.lowlight,
		'&.special': {
			color: '#CE2017'
		},
		'&.old': {
			marginLeft: 10,
			textDecoration: 'line-through',
			fontSize: 13,
			fontWeight: 500,
			color: '#999'
		},
		[theme.breakpoints.down('sm')]: {
			fontSize: 14
		}
	},
	labels: {
		position: 'absolute',
		top: 12,
		right: 12,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-end',
		gap: 6,
		[theme.breakpoints.down('sm')]: {
			top: 10,
			right: 5,
		},
	},
	discountBubble: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '4px 10px',
		fontSize: 12,
		fontWeight: 500,
		background: '#fff',
		color: '#000',
		pointerEvents: 'none',
		'&.light': {
			background: 'transparent',
			color: '#fff',
			border: '1px solid #fff'
		},
		'&.anniversary': {
			background: '#06172a',
			color: '#f9d59f'
		},
		'&.black-week': {
			background: 'black',
			color: 'white'
		},

		[theme.breakpoints.down('sm')]: {
			padding: '2px 8px',
			fontSize: 9
		}
	},
	marketingLabel: {
		position: 'absolute',
		top: 12,
		left: 12,
		zIndex: 10,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '4px 10px',
		fontSize: 13,
		fontWeight: 500,
		color: theme.palette.primary.color,
		backgroundColor: theme.palette.primary.background,
		pointerEvents: 'none',
		'&.light': {
			background: 'transparent',
			color: '#fff',
			border: '1px solid #fff',
			fontWeight: 600,
		},
		[theme.breakpoints.down('sm')]: {
			top: 10,
			left: 5,
			padding: '2px 8px',
			fontSize: 10
		}
	},
	purchaseButtonMinimal: {
		position: 'absolute',
		right: 8,
		bottom: 8,
		zIndex: 10,
		marginTop: 20,
		padding: 10,
		minWidth: 0,
        fontSize: 11,
        color: '#000',
        fontWeight: 600,
		textTransform: 'none',
		backgroundColor: 'transparent',
		borderRadius: '50%',
		'& svg': {
			marginTop: -1,
			color: '#000',
			transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
			fill: 'transparent',
		},
		'&:hover': {
			backgroundColor: 'transparent',
			mixBlendMode: 'multiply',
			'& svg': {
				fill: '#000',
				'& circle': {
					fill: '#fff',
				}
			}
		},
		'&.light': {
			backgroundColor: 'transparent',
			'& svg': {
				marginTop: -1,
				fill: 'transparent',
				color: '#fff',
				strokeWidth: 10,
				filter: 'drop-shadow(0px 0px 2px #000)',
			},
			'&:hover': {
				backgroundColor: 'transparent',
				mixBlendMode: 'screen',
				'& svg': {
					fill: '#fff',
					'& circle': {
						fill: '#000',
					}
				}
			},
		},
		[theme.breakpoints.down('sm')]: {
			padding: 8
		},
	},
	purchaseTouchRipple: {},
	subscribeButtonMinimal: {
		position: 'absolute',
		right: 8,
		bottom: 8,
		zIndex: 10,
		marginTop: 20,
		padding: 10,
		minWidth: 0,
		fontSize: 11,
		color: '#000',
		fontWeight: 500,
		textTransform: 'none',
		backgroundColor: 'transparent',
		'& svg': {
			marginTop: -1,
			color: '#000',
			fill: "transparent",
			transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
			strokeWidth: 0.25,
		},
		'&:hover': {
			mixBlendMode: 'multiply',
			backgroundColor: 'transparent',
			'& svg': {
				fill: '#000',
				strokeWidth: '1',
				'& path:nth-of-type(2)': {
					color: '#fff',
				}
			},
		},
		'&.light': {
			backgroundColor: 'transparent',
			'& svg': {
				marginTop: -1,
				color: '#fff',
				fill: "transparent",
				strokeWidth: '0.8',
				filter: 'drop-shadow(0px 0px 2px #000)',
			},
			'&:hover': {
				mixBlendMode: 'screen',
				backgroundColor: 'transparent',
				'& svg': {
					fill: '#fff',
					strokeWidth: '1',
					'& path:nth-of-type(2)': {
						color: '#000',
					}
				},
			},
		},
		[theme.breakpoints.down('sm')]: {
			padding: 8
		},
	},
	purchaseButton: {
		marginTop: 15,
		padding: '8px 24px',
        backgroundColor: 'transparent',
        fontSize: 10,
        color: theme.palette.primary.color,
        fontWeight: 600,
		borderRadius: 0,
		letterSpacing: 0.75,
		textDecoration: 'underline',
		'&:hover': {
			backgroundColor: theme.palette.primary.background,
		},
		'.xmas &': {
			backgroundColor: theme?.palette?.primary.main,
			'&:hover': {
				backgroundColor: theme?.palette?.primary.hover,
			},
		},
	},
	debugTool: {
		position: 'relative',
		zIndex: 10,
		top: 36,
		left: 8,
	},
	tooltipButton: {
		display: "inline-flex",
		justifyContent: "center",
		alignItems: "center",
		fontSize: "1.5em",
		position: 'absolute',
		zIndex: 50,
		background: '#fff',
		borderRadius: '50%',
        height: 28,
    	width: 28,
		cursor: 'pointer',
		'& + input[type="checkbox"]': {
			width: 0,
			height: 0,
			opacity: 0,
			position: 'absolute',
			'& + section': {
				position: "absolute",
				fontSize: "0.9em",
				width: '360px',
				padding: "1.5em 1em",
				left: '1.5rem',
				top: '1.5rem',
				zIndex: 99,
				display: 'none',
				marginTop: 0,
                flexDirection: 'column',
				background: '#fff',
				boxShadow: '0 0 10px rgba(0,0,0,0.2)',
				'& > pre': {
					background: "black",
					color: "white",
					wordBreak: 'break-word',
					whiteSpace: 'pre-wrap',
				}
			},
			'&:checked': {
				'& + section': {
					display: 'flex',
				}
			}
		},
	}
}), {name: 'ProductItem'});

export default function ProductItem({
		id,
		productNumber,
		name,
		image,
		link,
		prices,
		manufacturer,
		variants = false,
		available = true,
		deliveryTimeString = {},
		list,
		categoryBreadcrumb,
		sizes = {lg: 3, md: 3, sm: 6, xl: 3, xs: 6},
		position,
		addToCart = false,
		like = true,
		customClasses = {},
		customAttributes = {
			item: {}
		},
		lazyHydrate = true,
		elevateTicket = false,
		isMobile = false,
		isOutlet = false,
		clickCallback,
		propertyIds = [],
		streamIds = [],
		externalLabels = [],
		analytics = {},
		darkenImage = true
	}) {
    const router = useRouter();
	const formatPrice = useSessionStore((state) => state.methods.formatPrice);
	const salesChannel = useSessionStore((state) => state.methods.getSalesChannel());
	const classes = useStyles({classes: customClasses});
	const {t} = useTranslation();
	const {actions} = useContext(TrackingContext) || {};
	const cartMethods = useCartStore((state) => state.methods);
	const appMethods = useApplicationStore((state) => state.methods);
	const internalTool = Cookies.get('InternalTools');
	const [loading, setLoading] = useState(false);
	const hasLightButtons = propertyIds?.includes('12dba8fc3431593d890d5802b96bb751') ? 'light' : '';
	const shownInCart = elevateTicket ? 'in-cart' : '';
	const labels = externalLabels.length > 0 ? externalLabels : appMethods.getMarketingLabels(propertyIds);
	let bfSoldOut = '';
	let bfComingSoon = '';

	if (categoryBreadcrumb &&
		(categoryBreadcrumb.includes('christmas-super-deals') || categoryBreadcrumb.includes('super-deals') || categoryBreadcrumb.includes('black-week')) &&
		deliveryTimeString &&
		deliveryTimeString.id) {
        if (deliveryTimeString.id === '019257c5169f7d40983b28a28c4e72a3' || deliveryTimeString.id === '0192ae399fe970fc8c2a32844620bbec') {
		    bfSoldOut = 'bfSoldOut'
        }
        if (deliveryTimeString.id === '019257c452af7945b36efcd84ff71384') {
		    bfComingSoon = 'bfComingSoon'
        }
	}

	async function getProductAnalytics(id) {
		const result = await fetch(`/api/product/get/analytics-by-id/${id}`);
		const body = await result.json()
		return body.product?.extensions?.newport?.analytics
	}

	async function onItemInView() {
		if (!analytics.item_name) {
			analytics = await getProductAnalytics(id)
		}
		actions.productImpression({
			id: productNumber,
			analytics,
			price: prices.price,
			brand: manufacturer,
			discount: Math.abs(prices?.discount?.amount) || 0,
			list,
		});
	}

	function onClickTracking() {
		actions.productClick({
			id: productNumber,
			price: prices.price,
			discount: Math.abs(prices?.discount?.amount) || 0,
			brand: manufacturer,
			list,
			analytics
		});

		if(typeof clickCallback !== 'undefined' ) {
			clickCallback(productNumber);
		}
	}

	function isOutdoorInspiration() {
		return (!!(categoryBreadcrumb?.includes('Inspiration') || categoryBreadcrumb?.includes('Outlet')) && categoryBreadcrumb?.includes('Utemöbler'))
	}

	function isWeddingInspirationAndIsSlutsald() {
		return (categoryBreadcrumb?.includes('Wedding') && !available)
	}

	async function purchaseTracking() {
		actions.addToCart({
			id: productNumber,
			name: name,
			price: prices.price,
			discount: Math.abs(prices?.discount?.amount),
			brand: manufacturer,
			category: categoryBreadcrumb,
			list,
			variant: variants,
			quantity: 1,
			analytics
		});

		if (elevateTicket) {
			await elevateAddToCartNotification(
				elevateTicket,
				isMobile ? 'mobile' : 'desktop'
			)
		}
	}

    async function handlePurchase(e) {
        e.preventDefault();
        e.stopPropagation();

        setLoading(true);

        try {
            if (!variants) {
                const addToCartStatus = await cartMethods.addToCart({ productId: id, quantity: 1 });
                if (addToCartStatus) {
                    await purchaseTracking();
                }
                return;
            }

            const api = new StorefrontApiService();
            const response = await api.getVariants(id);

            /** @var ShopwareProduct */
            const { configurator } = response.product;
            const attributeContainsSize = configurator?.some((config) => config.translated?.customFields?.attributeCode?.includes('size'));

            if (configurator.length > 1 && attributeContainsSize) {
                await router.push(`${link}?from-list=${response.product.product.id}`);
            } else {
                const addToCartStatus = await cartMethods.addToCart({ productId: id, quantity: 1 });
                if (addToCartStatus) {
                    await purchaseTracking();
                }
            }
        } finally {
            setLoading(false);
        }
    }

	function renderDebugger() {
		return internalTool && (
			<div className={classes.debugTool}>
				<label htmlFor={"status-input-" + id} className={classes.tooltipButton}>ⓘ</label>
				<input type="checkbox" id={"status-input-" + id} />
				<section>
					<ProductTooling id={id} productNumber={productNumber}></ProductTooling>
					<pre>
                        {JSON.stringify({id,
							productNumber,
							name,
							link,
							prices,
							manufacturer,
							variants,
							available,
							list,
							categoryBreadcrumb,
							sizes,
							position,
							labels,
							like,
							customClasses,
							customAttributes,
							lazyHydrate,
							elevateTicket,
							isMobile,
							isOutlet,
							clickCallback}, null, 2)}
                    </pre>
				</section>
			</div>
		);
	}

    function renderPurchaseButton() {
        if (addToCart === 'minimal' && available) {
            return <Button
                className={[classes.purchaseButtonMinimal, 'add-to-cart', hasLightButtons].join(' ')}
                TouchRippleProps={{classes: {rippleVisible: classes.purchaseTouchRipple}}}
                onClick={(event) => {!loading && handlePurchase(event); return false;}}>
                <ShoppingBagFill width={18} height={18} alt={t('buy-short')} />
            </Button>
        }

        if (list?.name === 'checkout_upsell') {
            return <></>
        }

        return <Button className={[classes.subscribeButtonMinimal, hasLightButtons].join(' ')}>
            <Envelope width={18} height={18} alt={t('sold-out-subscribe')} />
        </Button>
    }

	function renderItem() {
		return (
            <>
                {' '}
                {renderDebugger()}
                <Link href={link}>
                    <a className={`${classes.link}`} onClick={onClickTracking} {...customAttributes.item}>
                        <div className={`sold-out-label ${bfSoldOut} ${bfComingSoon}`}>
                            <span>{deliveryTimeString.label}</span>
                        </div>
                        <div className={darkenImage ? classes.imageWrapper : ''}>
                            {image}

                            {renderPurchaseButton()}

                            <LikeButton
                                show={like}
                                productId={id}
                                product={{
                                    id,
                                    name,
                                    image,
                                }}
                                analyticsName={analytics?.item_name}
                                hasLightButtons={hasLightButtons}
                            ></LikeButton>

                            {variants ? (
                                <span className={[classes.moreVariantLabels, hasLightButtons, shownInCart].join(' ')}>
                                    <FilterNone fontSize={'inherit'} style={{ marginRight: 4 }} />
                                    {t('additional-variants')}
                                </span>
                            ) : (
                                ''
                            )}
                        </div>

                        {!isWeddingInspirationAndIsSlutsald() && (
                            <ProductLabels
                                available={available}
                                propertyIds={propertyIds}
                                streamIds={streamIds}
                                prices={prices}
                            ></ProductLabels>
                        )}

                        <div className={classes.info}>
                            {manufacturer ? <span className={classes.manufacturer}>{manufacturer}</span> : ''}

                            <h4 className={classes.name} dangerouslySetInnerHTML={{ __html: name }} title={name} />
                            <div className={classes.prices}>
                                {prices.isOnSale ? (
                                    <>
                                        <span className={[classes.price, 'special'].join(' ')}>
                                            {formatPrice(prices.price)}
                                        </span>
                                        <span className={[classes.price, 'old'].join(' ')}>
                                            {formatPrice(prices.listPrice)}
                                        </span>
                                    </>
                                ) : (
                                    <span className={classes.price}>{formatPrice(prices.price)}</span>
                                )}
                            </div>

                            <StockIndicator
                                style={{ marginTop: 10 }}
                                alternativeName={'plp'}
                                deliveryTime={deliveryTimeString}
                            />
                            {
                                salesChannel?.id &&
                                propertyIds?.length > 0
                                &&
                                <PhysicalStoreStockIndicator
                                    stockLevels={{}}
                                    propertyIds={propertyIds}
                                    placement='plp'
                                    showIcon={false}
                                />
                            }

                            {available && addToCart && addToCart !== 'minimal' ? (
                                <Button
                                    className={[classes.purchaseButton, 'add-to-cart', theme.mode].join(' ')}
                                    onClick={event => {
                                        !loading && handlePurchase(event);
                                        return false;
                                    }}
                                    disabled={loading}
                                >
                                    {t('buy-short')}
                                </Button>
                            ) : (
                                ''
                            )}
                        </div>
                    </a>
                </Link>
            </>
        );
	}

	return (
		<Grid
			item={true}
			{...sizes}
			classes={{
				root: classes.item
			}}
		>
			{lazyHydrate ?
				<LazyHydrate id={`ProductItem-${productNumber}-${position}`} on='visible' callback={onItemInView}>
					{ renderItem() }
				</LazyHydrate>
				:
				<TrackVisibility onVisible={onItemInView}>
					{renderItem()}
				</TrackVisibility>
			}
		</Grid>
	);
}
